/**
 * Utility functions for transcript operations
 */

import { supabase } from './supabase';
import { buildDisplayNameMap } from './speakerLabelUtils';

/**
 * Reconstruct a full transcript with speaker labels from speaker_segments.
 * This ensures consistent transcript format (with [HH:MM:SS] Speaker N: prefixes)
 * regardless of which UI path triggers assessment generation.
 *
 * Falls back to plain anonymized_text if no speaker segments exist.
 *
 * @param sessionId - The session ID to reconstruct transcript for
 * @returns The reconstructed transcript text and total duration in seconds
 */
export async function reconstructTranscriptWithSpeakerLabels(
  sessionId: string
): Promise<{ transcript: string; totalDurationSeconds: number }> {
  // Fetch transcripts (exclude archived)
  const { data: transcripts, error: transcriptError } = await supabase
    .from('transcripts')
    .select('id, anonymized_text, duration_seconds')
    .eq('session_id', sessionId)
    .or('is_archived.is.null,is_archived.eq.false')
    .order('created_at', { ascending: true });

  if (transcriptError || !transcripts || transcripts.length === 0) {
    throw new Error(
      `No transcripts found: ${transcriptError?.message || 'No transcripts available'}`
    );
  }

  // Fetch speaker segments for ALL transcripts
  const transcriptIds = transcripts.map((t: any) => t.id);
  const { data: allSegments } = await supabase
    .from('speaker_segments')
    .select('transcript_id, speaker_label, start_ms, start_time, text')
    .in('transcript_id', transcriptIds)
    .order('transcript_id', { ascending: true })
    .order('start_ms', { ascending: true });

  // Fetch speaker role mappings for display name normalization
  const { data: roleMappings } = await supabase
    .from('speaker_role_mappings')
    .select('speaker_label, speaker_role')
    .eq('session_id', sessionId);

  // Extract unique speaker labels from segments for hint-based normalization
  const uniqueLabels = [...new Set(
    (allSegments || []).map((s: any) => s.speaker_label).filter(Boolean)
  )] as string[];

  const displayNameMap = buildDisplayNameMap(roleMappings || [], uniqueLabels);

  const getStartSeconds = (seg: any): number => {
    if (seg.start_ms !== null && seg.start_ms !== undefined) {
      return seg.start_ms / 1000;
    }
    const timeStr = seg.start_time || '00:00:00';
    const parts = timeStr.split(':').map((p: string) => parseInt(p, 10));
    if (parts.length === 3 && !parts.some(isNaN)) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    return 0;
  };

  const formatTimestamp = (seconds: number): string => {
    if (seconds === null || seconds === undefined || isNaN(seconds)) return '';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `[${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}]`;
  };

  const reconstructSingle = (transcriptId: string): string => {
    const segments =
      allSegments?.filter((s: any) => s.transcript_id === transcriptId) || [];

    if (segments.length === 0) {
      const transcript = transcripts.find((t: any) => t.id === transcriptId);
      return transcript?.anonymized_text || '';
    }

    return segments
      .map((seg: any) => {
        const startSeconds = getStartSeconds(seg);
        const timestamp = formatTimestamp(startSeconds);
        const rawLabel = seg.speaker_label || 'Unknown';
        const speakerLabel = displayNameMap[rawLabel] || rawLabel;
        const prefix = timestamp ? `${timestamp} ` : '';
        return `${prefix}${speakerLabel}: ${seg.text}`;
      })
      .join('\n');
  };

  const getTranscriptLabel = (transcript: any, index: number): string => {
    const durationMin = transcript.duration_seconds
      ? Math.round(transcript.duration_seconds / 60)
      : 0;
    if (durationMin < 25) return `BRIEFING (Part ${index + 1})`;
    if (durationMin > 50) return `DEBRIEFING (Part ${index + 1})`;
    return `SESSION (Part ${index + 1})`;
  };

  const fullTranscript = transcripts
    .map((t: any, idx: number) => {
      const label = getTranscriptLabel(t, idx);
      const reconstructedText = reconstructSingle(t.id);
      const wordCount = reconstructedText.split(/\s+/).length;
      const durationMin = t.duration_seconds
        ? Math.round(t.duration_seconds / 60)
        : 0;

      return `\n=== ${label} ===\n[Word Count: ${wordCount} words | Duration: ${durationMin} minutes]\n\n${reconstructedText}\n`;
    })
    .join('\n');

  const totalDurationSeconds = transcripts.reduce(
    (sum: number, t: any) => sum + (t.duration_seconds || 0),
    0
  );

  return { transcript: fullTranscript, totalDurationSeconds };
}

/**
 * Poll the database to verify a transcript exists before triggering UI refresh.
 * This is more robust than using a fixed setTimeout delay because it adapts to
 * actual database performance and verifies data availability.
 *
 * @param transcriptId - The transcript ID to wait for
 * @param maxRetries - Maximum number of retry attempts (default: 5)
 * @param retryDelay - Delay between retries in ms (default: 200ms)
 * @returns Promise<boolean> - true if transcript was verified, false if not found after retries
 *
 * @example
 * const verified = await waitForTranscript(transcriptId);
 * if (verified) {
 *   // Proceed with UI update
 * } else {
 *   // Fall back to real-time subscription
 * }
 */
export async function waitForTranscript(
  transcriptId: string,
  maxRetries = 5,
  retryDelay = 200
): Promise<boolean> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const { data, error } = await supabase
      .from('transcripts')
      .select('id')
      .eq('id', transcriptId)
      .maybeSingle();

    if (!error && data) {
      console.log(`[TranscriptUtils] Transcript ${transcriptId} verified after ${attempt + 1} attempt(s)`);
      return true;
    }

    // Wait before next retry (unless this is the last attempt)
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  console.warn(`[TranscriptUtils] Transcript ${transcriptId} not found after ${maxRetries} attempts`);
  return false;
}
